import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Clock, AlertCircle, CheckCircle, XCircle, Filter } from "lucide-react";
import { ticketService } from "../../services/ticketService";
import { useAuth } from "../../hooks/useAuth";

interface Ticket {
  id: string;
  codigo: string;
  titulo: string;
  descricao: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA';
  status: 'ABERTO' | 'EM_ANDAMENTO' | 'AGUARDANDO_CLIENTE' | 'AGUARDANDO_APROVACAO' | 'RESOLVIDO' | 'FECHADO';
  createdAt: string;
  updatedAt: string;
  client: {
    id: string;
    razaoSocial: string;
    nomeFantasia?: string | null;
  };
  _count?: {
    apontamentos: number;
  };
}

const statusConfig = {
  ABERTO: {
    label: "Aberto",
    icon: AlertCircle,
    color: "text-red-500",
    bgColor: "bg-red-500/10 border-red-500/20 text-red-600"
  },
  EM_ANDAMENTO: {
    label: "Em Andamento",
    icon: Clock,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10 border-blue-500/20 text-blue-600"
  },
  AGUARDANDO_CLIENTE: {
    label: "Aguardando Cliente",
    icon: Clock,
    color: "text-yellow-500",
    bgColor: "bg-yellow-500/10 border-yellow-500/20 text-yellow-600"
  },
  AGUARDANDO_APROVACAO: {
    label: "Aguardando Aprovação",
    icon: AlertCircle,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10 border-purple-500/20 text-purple-600"
  },
  RESOLVIDO: {
    label: "Resolvido",
    icon: CheckCircle,
    color: "text-green-500",
    bgColor: "bg-green-500/10 border-green-500/20 text-green-600"
  },
  FECHADO: {
    label: "Fechado",
    icon: XCircle,
    color: "text-gray-500",
    bgColor: "bg-gray-500/10 border-gray-500/20 text-gray-600"
  }
};

const prioridadeConfig = {
  BAIXA: { label: "Baixa", color: "bg-green-500/10 border-green-500/20 text-green-600" },
  MEDIA: { label: "Média", color: "bg-yellow-500/10 border-yellow-500/20 text-yellow-600" },
  ALTA: { label: "Alta", color: "bg-red-500/10 border-red-500/20 text-red-600" }
};

export function PortalTickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [prioridadeFilter, setPrioridadeFilter] = useState<string>("all");
  const { user } = useAuth();

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await ticketService.list();
      setTickets(response);
    } catch (error) {
      console.error('Erro ao carregar tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.descricao.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;
    const matchesPrioridade = prioridadeFilter === "all" || ticket.prioridade === prioridadeFilter;
    
    return matchesSearch && matchesStatus && matchesPrioridade;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getClientDisplayName = () => {
    if (user?.role === 'client') {
      return user.name || "Cliente";
    }
    return "Cliente";
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Meus Tickets</h1>
            <p className="text-muted-foreground">Acompanhe seus chamados de suporte</p>
          </div>
        </div>
        
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 animate-pulse">
              <div className="space-y-3">
                <div className="h-4 bg-muted/50 rounded w-1/4"></div>
                <div className="h-6 bg-muted/50 rounded w-3/4"></div>
                <div className="h-4 bg-muted/50 rounded w-full"></div>
                <div className="h-4 bg-muted/50 rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Meus Tickets</h1>
          <p className="text-muted-foreground">
            Olá, {getClientDisplayName()}! Acompanhe seus chamados de suporte
          </p>
        </div>
        
        <Link 
          to="/portal/tickets/novo" 
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Novo Ticket
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Filtros</h3>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar por título, código ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-background/50 border border-border/50 focus:border-primary transition-colors rounded-lg text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>
          
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-background/50 border border-border/50 focus:border-primary transition-colors rounded-lg text-foreground w-full sm:w-48"
          >
            <option value="all">Todos os Status</option>
            <option value="ABERTO">Aberto</option>
            <option value="EM_ANDAMENTO">Em Andamento</option>
            <option value="AGUARDANDO_CLIENTE">Aguardando Cliente</option>
            <option value="RESOLVIDO">Resolvido</option>
            <option value="FECHADO">Fechado</option>
          </select>
          
          <select 
            value={prioridadeFilter} 
            onChange={(e) => setPrioridadeFilter(e.target.value)}
            className="px-4 py-2 bg-background/50 border border-border/50 focus:border-primary transition-colors rounded-lg text-foreground w-full sm:w-48"
          >
            <option value="all">Todas as Prioridades</option>
            <option value="ALTA">Alta</option>
            <option value="MEDIA">Média</option>
            <option value="BAIXA">Baixa</option>
          </select>
        </div>
      </div>

      {/* Lista de Tickets */}
      <div className="space-y-4">
        {filteredTickets.length === 0 ? (
          <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Nenhum ticket encontrado</h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || statusFilter !== "all" || prioridadeFilter !== "all"
                ? "Tente ajustar os filtros para encontrar seus tickets."
                : "Você ainda não possui tickets. Crie seu primeiro chamado!"}
            </p>
            <Link 
              to="/portal/tickets/novo" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Criar Primeiro Ticket
            </Link>
          </div>
        ) : (
          filteredTickets.map((ticket) => {
            const StatusIcon = statusConfig[ticket.status].icon;
            
            return (
              <Link 
                key={ticket.id} 
                to={`/portal/tickets/${ticket.id}`}
                className="block bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 group"
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-medium text-foreground group-hover:text-primary transition-colors">
                          {ticket.codigo}
                        </h3>
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${statusConfig[ticket.status].bgColor}`}>
                          <StatusIcon className="h-3 w-3" />
                          {statusConfig[ticket.status].label}
                        </div>
                      </div>
                      <p className="text-base font-normal text-foreground">
                        {ticket.titulo}
                      </p>
                    </div>
                    
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${prioridadeConfig[ticket.prioridade].color}`}>
                      {prioridadeConfig[ticket.prioridade].label}
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground line-clamp-2">
                    {ticket.descricao}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Criado em {formatDate(ticket.createdAt)}</span>
                    <span>
                      {ticket._count?.apontamentos ?? 0} {ticket._count?.apontamentos === 1 ? 'interação' : 'interações'}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}